/**
 * What a student reads when something goes wrong. The services speak in codes and exception
 * names (`quota_exceeded`, `encrypted`, `BudgetExceeded: paper ledger $2.06…`); none of that
 * reaches the screen. Each message says what happened and what to do next.
 */

const TRY_AGAIN = "That didn't work. Try again; if it keeps happening, tell us with Feedback.";

const API: Record<string, string> = {
  network: "Carrel couldn't be reached. Check your connection and try again.",
  quota_exceeded: "That needs more than you have left this month.",
  subjects_limit: "Your plan has no room for another subject.",
  not_entitled: "Your plan doesn't include that.",
  invalid_chapters:
    "Some chapters overlap or run past the end of the book. Check the page numbers.",
  exemplars_missing:
    "The questions from your past papers changed while you were editing. Reload the page and try again.",
  unknown_chapter:
    "One of those chapters has no flashcards yet. Add it from the subject page, or untick it.",
  no_chapters: "Tick at least one chapter.",
  invalid_length: "Choose a paper length.",
  terms_version_stale: "The terms changed while you were reading. Reload the page to see them.",
  invalid_profile: "Part of the format doesn't add up. Check the marks and the question styles.",
};

/** An API error code, in words. A `*_not_found` means it was deleted, or never this student's. */
export function apiMessage(code: string): string {
  if (code in API) return API[code] as string;
  if (code === "not_found" || code.endsWith("_not_found"))
    return "That isn't here any more; it may have been deleted. Go back and reload.";
  return TRY_AGAIN;
}

const REJECTED: Record<string, string> = {
  encrypted: "The PDF is password-protected. Remove the password and upload it again.",
  not_a_pdf: "This isn't a PDF. Save it as a PDF and upload it again.",
  unreadable: "The PDF couldn't be opened. Download it again and upload the new copy.",
  file_too_large: "The file is over 250 MB.",
};

/** Why an upload was turned away. */
export function rejectMessage(reason: string | null): string {
  return (reason && REJECTED[reason]) || "The file couldn't be used. Try uploading it again.";
}

const PAPER_FAILED: Record<string, string> = {
  BudgetExceeded: "Writing it ran over its limit, so it was stopped.",
  EmptyPaper: "No question passed the accuracy check.",
  PaperError: "The questions couldn't be put together into a paper.",
  "States.Timeout": "It took too long to write, so it was stopped.",
};

/**
 * Why a paper was not written. The service sends `ExceptionName: detail`; a known name gets its
 * sentence, an unknown one a plain one. Text that is already a sentence is shown as it is, with a full stop.
 */
export function paperFailure(reason: string | null): string {
  const fallback = "Something went wrong while it was being written.";
  if (!reason) return fallback;
  const name = /^([A-Z][\w.]*): /.exec(reason)?.[1];
  if (name === undefined) return /[.!?]$/.test(reason) ? reason : `${reason}.`;
  return PAPER_FAILED[name] ?? fallback;
}
